/* גיבוי מלא לקובץ JSON, ושחזור שמוסיף רק מה שחסר (לא מוחק ולא דורס) */
import { S, SETTINGS } from '../../domain/state.js';
import { sb, q } from '../../lib/supabase.js';
import { todayISO, iso10, absAmt } from '../../lib/format.js';
import { getTxSrc } from '../../domain/tx.js';
import { insertTx, recWrite, loadAll } from '../../store/actions.js';
import { toast, ls } from '../../store/ui.js';
import { confirmDialog } from '../../components/ui.jsx';

export async function exportBackup() {
  try {
    const settings = await q(sb().from('settings').select('*')).catch(() =>
      Object.keys(SETTINGS).map((k) => ({ key: k, value: SETTINGS[k].ser(S.set[SETTINGS[k].field]) })));
    const data = {
      version: 2, exported_at: new Date().toISOString(), settings,
      categories: S.cats, goals: S.budgets, recurring: S.rec, commitments: S.commits, savings_goals: S.savings, transactions: S.tx,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'budget-backup-' + todayISO() + '.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    ls.set('last_backup', new Date().toISOString());
    toast('✅ גיבוי נוצר (' + S.tx.length + ' פעולות, ' + S.rec.length + ' חיובים קבועים)');
  } catch (e) {
    toast('שגיאה בייצוא: ' + e.message);
  }
}

export async function importBackup(file) {
  let data;
  try { data = JSON.parse(await file.text()); } catch (e) { return toast('שגיאה בקריאת הקובץ: ' + e.message); }
  if (!data || !data.version || !Array.isArray(data.transactions)) return toast('קובץ לא תקין - חסרים שדות');
  const n = (a) => (a || []).length;
  const ok = await confirmDialog({
    title: 'ייבוא גיבוי', okLabel: 'ייבא',
    body: 'נמצאו בגיבוי: ' + n(data.transactions) + ' פעולות, ' + n(data.recurring) + ' חיובים קבועים, ' + n(data.categories) + ' קטגוריות, ' + n(data.savings_goals) + ' יעדי חיסכון. ' +
      'רק מה שלא קיים בחשבון יתווסף. שום דבר קיים לא יימחק או יידרס.' + (data.settings ? ' הגדרות ישוחזרו רק אם הן עוד לא מוגדרות.' : ''),
  });
  if (!ok) return;
  toast('מייבא...');
  const st = { tx: 0, rec: 0, cat: 0, goal: 0, sav: 0, com: 0, set: 0, err: 0 };
  const db = sb();
  const put = async (table, body) => { try { return (await q(db.from(table).insert(body).select()))[0]; } catch (e) { st.err++; console.error('import', table, e); return null; } };

  // הגדרות: רק מפתח שעוד לא קיים, כדי לא לדרוס מצב עדכני בגיבוי ישן
  if (Array.isArray(data.settings)) {
    const cur = await q(db.from('settings').select('key')).catch(() => []);
    const have = new Set(cur.map((r) => r.key));
    for (const s of data.settings) {
      if (!s || !s.key || have.has(s.key) || s.value == null) continue;
      try { await q(db.from('settings').upsert({ key: s.key, value: String(s.value) })); st.set++; } catch { st.err++; }
    }
  }
  const catHave = new Set(S.cats.map((c) => c.id));
  for (const c of data.categories || []) {
    if (!c || catHave.has(c.id)) continue;
    if (await put('categories', { id: c.id, label: c.label, icon: c.icon, color: c.color, type: c.type, keywords: c.keywords || null })) st.cat++;
  }
  const recKey = (r) => [r.type, r.description, Math.round(Number(r.amount) || 0), r.day_of_month].join('|');
  const recHave = new Set(S.rec.map(recKey));
  for (const r of data.recurring || []) {
    if (!r || recHave.has(recKey(r))) continue;
    const rb = { description: r.description, amount: r.amount, type: r.type, cat: r.cat, day_of_month: r.day_of_month, source: r.source || 'cash' };
    if (Number(r.every_months) > 1) { rb.every_months = r.every_months; rb.start_month = r.start_month; }
    try { await recWrite(null, rb); st.rec++; } catch { st.err++; }
  }
  const gHave = new Set(S.budgets.map((g) => g.cat + '|' + g.month));
  for (const g of data.goals || []) {
    if (!g || gHave.has(g.cat + '|' + g.month)) continue;
    if (await put('budget_goals', { cat: g.cat, month: g.month, amount: g.amount })) st.goal++;
  }
  const sHave = new Set(S.savings.map((g) => g.name));
  for (const v of data.savings_goals || []) {
    if (!v || sHave.has(v.name)) continue;
    if (await put('savings_goals', { name: v.name, target: v.target, saved: v.saved || 0, emoji: v.emoji, color: v.color, deadline: v.deadline || null })) st.sav++;
  }
  const cHave = new Set(S.commits.map((c) => c.description + '|' + c.due_date));
  for (const m of data.commitments || []) {
    if (!m || cHave.has(m.description + '|' + m.due_date) || String(m.due_date) < todayISO()) continue;
    if (await put('commitments', { description: m.description, amount: m.amount, due_date: m.due_date, kind: m.kind })) st.com++;
  }
  // פעולות: מזהה לפי תאריך, סכום, תיאור וסוג
  const txKey = (t) => [iso10(t.date), Math.round(absAmt(t) * 100), t.description || '', t.type || ''].join('|');
  const txHave = new Set(S.tx.map(txKey));
  for (const t of data.transactions) {
    if (!t || txHave.has(txKey(t))) continue;
    const body = { description: t.description, amount: t.amount, type: t.type, cat: t.cat, date: t.date, month: t.month, source: getTxSrc(t) };
    if (t.excluded === true) body.excluded = true; // חיוב כרטיס בעו"ש, העברה פנימית: אחרת ייספרו פעמיים
    try { await insertTx(body, { ref: t.ref, raw: t.raw_name, card4: t.card4, orig: t.orig_amount }); txHave.add(txKey(t)); st.tx++; } catch (e) { st.err++; console.error(e); }
  }
  toast('✅ שוחזרו: ' + st.tx + ' פעולות, ' + st.rec + ' חיובים קבועים, ' + st.cat + ' קטגוריות, ' + st.sav + ' יעדי חיסכון, ' + st.set + ' הגדרות' + (st.err ? ' · ⚠️ ' + st.err + ' נכשלו' : ''));
  await loadAll();
}
